import { useEffect, useRef } from 'react'

import { getItemsTotals } from 'routes/probate-estate/fact-find/get-totals'
import {
  ProbateEstateItem,
  ProbateEstateProperty,
  ProbateEstatePropertiesAttributes,
} from 'lib/models/probate-estate'

import CollectionSection from '../../components/collection-section'
import SectionTotals from '../../components/section-totals'
import Property, { PropertyUpdate } from './property'

type Props = {
  properties: ProbateEstateProperty[]
  createProperty: (
    attributes: Partial<ProbateEstatePropertiesAttributes>
  ) => void
  updateProperty: (id: number, attributes: PropertyUpdate) => void
  deleteProperty: (id: number) => void
  isCreating: boolean
  isLoaded: boolean
}

const Properties = ({
  properties,
  createProperty,
  updateProperty,
  deleteProperty,
  isCreating,
  isLoaded,
}: Props) => {
  // properties added before the questionnaire aren't flagged, so the
  // first one stands in as the main residence
  const mainResidence =
    properties.find((property) => property.attributes.isMainResidence) ??
    properties[0]
  const additionalProperties = properties.filter(
    (property) => property !== mainResidence
  )
  const sortedProperties = mainResidence
    ? [mainResidence, ...additionalProperties]
    : []

  // the main residence can't be deleted, so it always exists - which is
  // also why the section has no toggle
  const hasRequestedMainResidence = useRef(false)
  useEffect(() => {
    if (!isLoaded || properties.length > 0) return
    if (hasRequestedMainResidence.current) return
    hasRequestedMainResidence.current = true
    // the collection hook shows a failure's error, so it's left there
    // rather than going unhandled
    Promise.resolve(
      createProperty({ isMainResidence: true, ownership: 'sole' })
    ).catch(() => undefined)
  }, [isLoaded, properties.length, createProperty])

  const { total, sole, joint } = getItemsTotals(
    properties as unknown as ProbateEstateItem[]
  )

  return (
    <CollectionSection
      heading="2.1 Properties"
      type="Property"
      records={sortedProperties}
      isAdding={isCreating}
      onAdd={() =>
        createProperty({ isMainResidence: false, ownership: 'sole' })
      }
      addLabel="Add property"
      onDelete={deleteProperty}
      summary={<SectionTotals total={total} sole={sole} joint={joint} />}
      fullWidth
      getCardHeading={(record) => {
        if (record === mainResidence) return 'Main residence'
        const position =
          additionalProperties.indexOf(record as ProbateEstateProperty) + 1
        return `Additional property ${position} of ${additionalProperties.length}`
      }}
      canDelete={(record) => record !== mainResidence}
      renderRow={(record) => (
        <Property
          property={record as ProbateEstateProperty}
          isMainResidence={record === mainResidence}
          updateProperty={updateProperty}
        />
      )}
    />
  )
}

export default Properties
